import { Bot, Building2, Files, ListChecks, MessageSquare, ReceiptText } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router'

import { ToggleTabs } from '@/components/ui/ToggleTabs'
import { type SalesLead } from '@/models/salesLead'
import { ActivityTab } from './ActivityTab'
import { AgentChatTab } from './AgentChatTab'
import { CarriersTab } from './CarriersTab'
import { DocumentsTab } from './DocumentsTab'
import { EstimatesTab } from './EstimatesTab'
import { SequencesTab } from './SequencesTab'

const DEFAULT_TAB = 'activity'

interface SalesLeadDetailTabsProps {
  lead: SalesLead
}

export function SalesLeadDetailTabs({ lead }: SalesLeadDetailTabsProps) {
  const location = useLocation()
  const navigate = useNavigate()

  const rawHash = location.hash.slice(1)
  // A global-search deep link uses `#activity-<id>` to target one activity;
  // treat it as the activity tab so the card can be scrolled to + flashed.
  const currentTab = rawHash.startsWith('activity-') ? 'activity' : rawHash || DEFAULT_TAB

  function onTabChange(value: string) {
    void navigate(
      { search: location.search, hash: value === DEFAULT_TAB ? '' : value },
      { replace: true, state: location.state, preventScrollReset: true }
    )
  }

  return (
    <ToggleTabs
      value={currentTab}
      onValueChange={onTabChange}
      className="w-full"
      listClassName="mb-1"
      items={[
        { value: 'activity', icon: <MessageSquare />, label: 'Activity' },
        { value: 'carriers', icon: <Building2 />, label: 'Carriers' },
        { value: 'estimates', icon: <ReceiptText />, label: 'Estimates' },
        { value: 'agent-chat', icon: <Bot />, label: 'Agent' },
        { value: 'sequences', icon: <ListChecks />, label: 'Sequences' },
        { value: 'documents', icon: <Files />, label: 'Documents' },
      ]}
    >
      {currentTab === 'activity' && <ActivityTab lead={lead} />}
      {currentTab === 'carriers' && <CarriersTab lead={lead} />}
      {currentTab === 'estimates' && <EstimatesTab lead={lead} />}
      {currentTab === 'agent-chat' && <AgentChatTab lead={lead} />}
      {currentTab === 'sequences' && <SequencesTab lead={lead} />}
      {currentTab === 'documents' && <DocumentsTab lead={lead} />}
    </ToggleTabs>
  )
}
